import { FormControlLabel, Switch, TextField } from '@mui/material';
import {
  EditAppBar,
  FieldValidator,
  NonEditableField,
  PageWithDrawer,
  getRequiredErrorMessage,
  objectsAreDifferents,
} from '@prismamedia/one-components';
import { FC, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  BrandKey,
  CreateSegmentMutation,
  GetSegmentQuery_query_segment_Segment,
  SegmentStatus,
} from '../../../__generated__/queries-web';
import { replaceParams } from '../../../routing/Router';
import { paths } from '../../../routing/Router/paths';
import { useGlobalBrandKey } from '../../../utils/globalState';
import { useGetSegment } from './getSegment.web.graphql';
import { useStyles } from './styles';
import {
  useCreateSegment,
  useUpdateSegment,
} from './updateSegment.web.graphql';

const getEmptyItem = (
  brandKey: BrandKey,
): GetSegmentQuery_query_segment_Segment => ({
  active: true,
  __typename: 'Segment',
  id: '',
  brandKey,
  title: '',
  ios: null,
  android: null,
  status: SegmentStatus.Published,
  editorialAlert: null,
});

const saveMandatoryFields: FieldValidator<
  GetSegmentQuery_query_segment_Segment
>[] = [
  {
    label: 'Titre',
    validate: ({ title }) => !!title,
  },
];

export const SegmentEditPage: FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const [brandKey] = useGlobalBrandKey();
  const [item, setItem] = useState<
    GetSegmentQuery_query_segment_Segment | undefined
  >(undefined);
  const { id } = useParams();
  const isCreate = id === 'new';
  const { data, loading, error } = useGetSegment(id, isCreate);
  const [saveLoading, setSaveLoading] = useState(false);
  const updateItem = useUpdateSegment();
  const createItem = useCreateSegment();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const emptyItem = useMemo(() => getEmptyItem(brandKey!), [brandKey]);
  const itemDiff = objectsAreDifferents(
    item,
    isCreate ? emptyItem : data?.segment,
  );
  const requiredFields = getRequiredErrorMessage<
    GetSegmentQuery_query_segment_Segment
  >(item, saveMandatoryFields);

  useEffect(() => {
    if (isCreate) {
      setItem(emptyItem);
    } else if (data?.segment) {
      setItem({
        ...data.segment,
        ios: data.segment.ios
          ? typeof data.segment.ios === 'object'
            ? JSON.stringify(data.segment.ios, null, 2)
            : data.segment.ios
          : null,
        android: data.segment.android
          ? typeof data.segment.android === 'object'
            ? JSON.stringify(data.segment.android, null, 2)
            : data.segment.android
          : null,
        editorialAlert: data.segment.editorialAlert
          ? typeof data.segment.editorialAlert === 'object'
            ? JSON.stringify(data.segment.editorialAlert, null, 2)
            : data.segment.editorialAlert
          : null,
      });
    }
  }, [isCreate, data, emptyItem]);

  const onSave = async () => {
    if (item) {
      setSaveLoading(true);
      const res = isCreate
        ? await createItem({
            title: item.title,
            brandKey: item.brandKey,
            ios: item.ios
              ? typeof item.ios === 'string'
                ? JSON.parse(item.ios)
                : item.ios
              : null,
            android: item.android
              ? typeof item.android === 'string'
                ? JSON.parse(item.android)
                : item.android
              : null,
            status: item.status,
            editorialAlert: item.editorialAlert
              ? typeof item.editorialAlert === 'string'
                ? JSON.parse(item.editorialAlert)
                : item.editorialAlert
              : null,
            active: true,
          })
        : await updateItem(item.id, {
            title: item.title,
            ios: item.ios
              ? typeof item.ios === 'string'
                ? JSON.parse(item.ios)
                : item.ios
              : null,
            android: item.android
              ? typeof item.android === 'string'
                ? JSON.parse(item.android)
                : item.android
              : null,
            status: item.status,
            editorialAlert: item.editorialAlert
              ? typeof item.editorialAlert === 'string'
                ? JSON.parse(item.editorialAlert)
                : item.editorialAlert
              : null,
            active: item.active,
          });
      setSaveLoading(false);
      if (res && isCreate) {
        history.replace(
          replaceParams(paths.SEGMENT_EDIT, {
            id: (res.data as CreateSegmentMutation).createSegment.id,
          }),
        );
      }
    }
  };

  const validateJsonString = (value: string | null): boolean => {
    if (!value) return true;
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  };

  const hasInvalidJson =
    item &&
    ((item.ios && !validateJsonString(item.ios)) ||
      (item.android && !validateJsonString(item.android)) ||
      (item.editorialAlert && !validateJsonString(item.editorialAlert)));

  return (
    <>
      <EditAppBar
        title={
          isCreate
            ? item?.title || 'Nouveau segment'
            : data?.segment?.title || ''
        }
        onNavigateBack={() => history.push(paths.SEGMENT_LIST)}
        save={{
          disabled: (!itemDiff && !isCreate) || hasInvalidJson,
          loading: saveLoading,
          onClick: onSave,
          requiredFields,
        }}
      />
      <PageWithDrawer
        loading={loading}
        error={
          error ||
          (!item && !data?.segment
            ? Error("Ce segment n'existe pas")
            : undefined)
        }
        rightDrawer={
          <NonEditableField
            className={classes.field}
            label="Marque"
            value={item?.brandKey}
          />
        }
      >
        {item && (
          <>
            <TextField
              className={classes.field}
              label="Titre"
              onChange={({ target: { value } }) => {
                setItem((prev) => (prev ? { ...prev, title: value } : prev));
              }}
              value={item.title}
              variant="standard"
            />
            <TextField
              className={classes.field}
              label="Query iOS"
              multiline
              onChange={({ target: { value } }) => {
                setItem((prev) => (prev ? { ...prev, ios: value } : prev));
              }}
              value={item.ios || ''}
              variant="standard"
            />
            <TextField
              className={classes.field}
              label="Query Android"
              multiline
              onChange={({ target: { value } }) => {
                setItem((prev) => (prev ? { ...prev, android: value } : prev));
              }}
              value={item.android || ''}
              variant="standard"
            />

            <TextField
              className={classes.field}
              label="Query Web"
              multiline
              onChange={({ target: { value } }) => {
                setItem((prev) =>
                  prev ? { ...prev, editorialAlert: value } : prev,
                );
              }}
              value={item.editorialAlert || ''}
              variant="standard"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={item.status === SegmentStatus.Published}
                  onChange={({ target: { checked } }) => {
                    setItem((prev) =>
                      prev
                        ? {
                            ...prev,
                            status: checked
                              ? SegmentStatus.Published
                              : SegmentStatus.Draft,
                          }
                        : prev,
                    );
                  }}
                />
              }
              label={
                item.status === SegmentStatus.Published ? 'Activé' : 'Désactivé'
              }
            />
          </>
        )}
      </PageWithDrawer>
    </>
  );
};
